import { AvailabilityOverlappingFault, AvailabilityUpdateCommand } from '@dreryk/egabinet-appointment-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { personnelTranslationKeys } from '../../../translations';
import { useCreateOverlappingAvailabilityErrorMessage } from '../../common/common.hooks';
import {
   TIME_SLOT_NONE,
   TIME_SLOT_OTHER,
} from '../../common/fields/AvailabilityTimeSlot/AvailabilityTimeSlotFixed/AvailabilityTimeSlotFixed.types';
import { AvailabilityUpdateFormBean } from '../availabilityUpdate.types';
import { getBookingChannels } from '../../../schedule/common/common.utils';

type UpdateAvailabilityInFormViewParameters = AvailabilityUpdateFormBean & { id: number };

export const useUpdateAvailabilityInFormView = (
   practitionerId: PractitionerId,
   refetchAvailabilities: VoidFunction,
   onSettledAction: () => void
): UseCqrsCommandResult<void, UpdateAvailabilityInFormViewParameters> => {
   const createOverlappingAvailabilityErrorMessage = useCreateOverlappingAvailabilityErrorMessage();

   return useCqrsCommand<void, UpdateAvailabilityInFormViewParameters, AvailabilityOverlappingFault>(
      (parameters) => {
         const command = new AvailabilityUpdateCommand();

         const slotDuration =
            parameters.slotDurationFixed === TIME_SLOT_OTHER
               ? parameters.slotDurationManual
               : parameters.slotDurationFixed;
         command.id = parameters.id;
         command.startTime = parameters.startTime;
         command.endTime = parameters.endTime;
         command.availabilityTypeId = Number(parameters.availabilityTypeId!.id);
         command.updateSingleOccurrence = parameters.updateSingleOccurrence;
         command.updatingAvailabilityDate = parameters.availabilityDate;
         command.slotDurationInMinutes = Choice.withUseDefaultIfNull(
            slotDuration === TIME_SLOT_NONE ? null : slotDuration
         );
         command.practitionerId = practitionerId;
         command.location = parameters.healthcareProviderLocation!.locationIdentifier;
         command.bookingChannels = getBookingChannels(parameters);

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(personnelTranslationKeys.successfullyUpdatedAvailability);
            refetchAvailabilities();
            onSettledAction();
         },
         onFault: {
            'egabinet-appointment-frontend-api:AvailabilityOverlappingFault': (fault) =>
               dispatchSnackbarNegativeEvent(createOverlappingAvailabilityErrorMessage(fault)),
         },
      }
   );
};
